import React, { useState, useEffect, useRef, useCallback } from 'react';

const SURVIVOR_WORDS = ['OUTWIT', 'OUTPLAY', 'OUTLAST'];

export default function PullToRefresh({ onRefresh, children, debug = false }) {
  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const touchStartY = useRef(0);
  const touchStartScrollTop = useRef(0);
  const containerRef = useRef(null);

  const PULL_THRESHOLD = 80; // Distance to trigger refresh
  const MAX_PULL = 120; // Max distance to pull

  useEffect(() => {
    // Rotate through words every 300ms while pulling or refreshing
    if (pullDistance > 0 || isRefreshing) {
      const interval = setInterval(() => {
        setCurrentWordIndex((prev) => (prev + 1) % SURVIVOR_WORDS.length);
      }, 300);
      return () => clearInterval(interval);
    }
  }, [pullDistance, isRefreshing]);

  // Check if at top of scroll
  const isAtTop = useCallback(() => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0;
    return scrollTop <= 0;
  }, []);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleTouchStart = (e) => {
      // Only allow pull-to-refresh if scrolled to top
      if (isAtTop()) {
        touchStartY.current = e.touches[0].clientY;
        touchStartScrollTop.current = window.pageYOffset || document.documentElement.scrollTop || 0;
      } else {
        touchStartY.current = 0;
      }
    };

    const handleTouchMove = (e) => {
      if (isRefreshing || touchStartY.current === 0) return;

      const touchY = e.touches[0].clientY;
      const distance = touchY - touchStartY.current;

      // Only pull down when at top and pulling downward
      if (distance > 0 && isAtTop()) {
        // Prevent default scroll behavior to enable pull effect
        e.preventDefault();
        
        // Apply resistance effect (gets harder to pull as distance increases)
        const resistanceFactor = 0.5;
        const adjustedDistance = Math.min(distance * resistanceFactor, MAX_PULL);
        setPullDistance(adjustedDistance);
      }
    };

    const handleTouchEnd = async () => {
      if (isRefreshing) return;

      if (pullDistance >= PULL_THRESHOLD) {
        setIsRefreshing(true);
        
        try {
          // Call the refresh callback
          if (onRefresh) {
            await onRefresh();
          }
          
          // Show refreshing state for at least 500ms for UX
          await new Promise(resolve => setTimeout(resolve, 500));
        } catch (error) {
          console.error('Refresh failed:', error);
        } finally {
          setIsRefreshing(false);
        }
      }

      // Reset
      setPullDistance(0);
      touchStartY.current = 0;
    };

    // Use document-level listeners for better mobile support
    document.addEventListener('touchstart', handleTouchStart, { passive: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isRefreshing, pullDistance, onRefresh, isAtTop]);

  const pullProgress = Math.min((pullDistance / PULL_THRESHOLD) * 100, 100);
  const opacity = Math.min(pullDistance / 60, 1);

  return (
    <div
      ref={containerRef}
      style={{ 
        position: 'relative',
        overscrollBehavior: 'contain',
        WebkitOverflowScrolling: 'touch',
        minHeight: '100%',
      }}
    >
      {/* Debug indicator (only show if debug prop is true) */}
      {debug && (
        <div style={{
          position: 'fixed',
          top: 60,
          right: 10,
          background: 'red',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '8px',
          zIndex: 9999,
          fontSize: '12px',
          fontWeight: 'bold',
        }}>
          Pull: {Math.round(pullDistance)} | Refreshing: {isRefreshing ? 'YES' : 'NO'} | AtTop: {isAtTop() ? 'YES' : 'NO'}
        </div>
      )}
      
      {/* Pull to refresh indicator */}
      <div
        style={{
          position: 'fixed',
          top: 56, // Below the navbar
          left: 0,
          right: 0,
          height: pullDistance || (isRefreshing ? 60 : 0),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: isRefreshing ? 'height 0.2s ease' : 'none',
          opacity: isRefreshing ? 1 : opacity,
          pointerEvents: 'none',
          zIndex: 50,
          backgroundColor: 'rgba(245, 240, 230, 0.95)', // Cream background matching theme
        }}
      >
        <div className="text-center">
          {/* Rotating Survivor word */}
          <div
            className="font-bold text-2xl tracking-wider transition-all duration-300"
            style={{
              color: '#FF7F50', // Coral/Orange from theme
              transform: `scale(${Math.min(pullProgress / 100, 1)})`,
            }}
          >
            {SURVIVOR_WORDS[currentWordIndex]}
          </div>
          
          {/* Progress indicator */}
          {!isRefreshing && pullDistance > 20 && (
            <div className="mt-2 flex items-center justify-center gap-1">
              {[0, 1, 2].map((i) => (
                <div
                  key={i}
                  className="w-2 h-2 rounded-full transition-all duration-200"
                  style={{
                    backgroundColor: pullProgress >= (i + 1) * 33 ? '#FF7F50' : '#1E3A4C33',
                  }}
                />
              ))}
            </div>
          )}
          
          {/* Refreshing spinner */}
          {isRefreshing && (
            <div className="mt-2 flex justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#FF7F50] border-t-transparent"></div>
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div
        style={{
          transform: `translateY(${pullDistance || (isRefreshing ? 60 : 0)}px)`,
          transition: isRefreshing || pullDistance === 0 ? 'transform 0.2s ease' : 'none',
        }}
      >
        {children}
      </div>
    </div>
  );
}
